import { ENGRAVING_BASE_FONT_PX, ENGRAVING_MAX_CHARS } from "../../../shared/api-contract";
import { useEffect, useRef, useState } from "react";
import { useRing } from "../ringContext";
import { useCaps } from "../../editor/CapsProvider";
import { Button, Chip, ColorField, Disclosure, Field, FileButton, SectionLabel, Select, Slider, TextField, Toggle } from "../../kit";
import type { TextFieldHandle } from "../../kit";
import type { EngravingLogoMode, EngravingSide } from "../../../shared/api-contract";
import { uploadAsset } from "../../../features/assets/assetsApi";
import {
  LOGO_HEIGHT,
  LOGO_RASTER_MAX_BYTES,
  isLocalOnlyLogo,
  logoFrom,
  logoViewBox,
  rasterKind,
  readFileAsDataUrl,
  svgFileToLogoPath,
  vaultSignedIn,
} from "./logoUi";

/**
 * The band panel's hard-coded family list, verbatim, plus one entry that carries
 * the engagement half's `cursive` font (see the note on the component). The band
 * engraving canvas quotes any family containing a space, so a single family name
 * — not a CSS stack — is required here.
 *
 * These are system families: present on the machine, nothing to fetch.
 */
const SYSTEM_FONTS = [
  { id: "Georgia", label: "Georgia" },
  { id: "Times New Roman", label: "Times" },
  { id: "Garamond", label: "Garamond" },
  { id: "Helvetica", label: "Helvetica" },
  { id: "Verdana", label: "Verdana" },
  { id: "Courier New", label: "Courier" },
  { id: "Brush Script MT", label: "Script" },
];

/**
 * v42 — the reference's six families, ADDED beside ours rather than replacing
 * them (a saved design still names Georgia and must still get Georgia).
 *
 * They are web fonts, which the DECAL needs in the viewer document, not this
 * one: `band/engravingFonts.ts` fetches the family there before drawing and
 * redraws once it lands. The copy loaded below is only so the picker previews
 * each name in its own face — and it is the same Google Fonts source, so the
 * preview and the engraving are the same typeface.
 *
 * ponytail: CDN-hosted, like the rest of the app's type. Self-host the six WOFF2
 * files the day an offline/air-gapped deployment needs them.
 */
const WEB_FONTS = [
  { id: "Inter", label: "Inter" },
  { id: "Aboreto", label: "Aboreto" },
  { id: "Dancing Script", label: "Dancing Script" },
  { id: "Montserrat", label: "Montserrat" },
  { id: "Nunito", label: "Nunito" },
  { id: "Sacramento", label: "Sacramento" },
];

const FONTS = [...SYSTEM_FONTS, ...WEB_FONTS];

const WEB_FONT_SPEC =
  "family=Inter:wght@400;700&family=Aboreto&family=Dancing+Script:wght@400;700" +
  "&family=Montserrat:wght@400;700&family=Nunito:wght@400;700&family=Sacramento";

/** Inject the preview stylesheet once, on first visit to this tab — the other
 * six tabs and every Vault page pay nothing for a font only used here. */
function usePreviewFonts() {
  useEffect(() => {
    const id = "ring-engraving-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${WEB_FONT_SPEC}&display=swap`;
    document.head.appendChild(link);
    // Deliberately not removed on unmount: the sheet is idempotent and the tab
    // is re-entered constantly.
  }, []);
}

const STYLES = [
  { id: "normal", label: "Regular" },
  { id: "bold", label: "Bold" },
];

/** Everything here renders on a 2D canvas decal, so any glyph the chosen family
 * has works. These sixteen are the reference's palette, in its order. */
const SYMBOLS = ["♡", "♥", "❥", "∞", "⚭", "♢", "♀", "♂", "♫", "♪", "☯", "✷", "✦", "★", "☾", "—"];

/** Shared with the engine clamp in `BandConfigurator.setEngravingText`. */
const ENGRAVING_MAX = ENGRAVING_MAX_CHARS;

const SIDES: { id: EngravingSide; label: string }[] = [
  { id: "left", label: "Left" },
  { id: "center", label: "Middle" },
  { id: "right", label: "Right" },
];

/**
 * Engrave tab — the band `EngravingStep`, unchanged.
 *
 * The engagement half's engraving (≤ 14 chars + `block | cursive`) was a strict
 * subset of this panel, so it was folded in rather than duplicated: the band's
 * engraving engine (`BandConfigurator.rebuildEngraving`) is the only thing that
 * renders one. `EngagementExtras` no longer declares those fields at all — the
 * band limit (40) wins, and a persisted design that still carries them has them
 * migrated onto the band half on load. "Script" is where the legacy `cursive`
 * font lands; every other family is the `block` case.
 */
export function EngraveTab() {
  const { state, r, apply } = useRing();
  const e = state.engraving;
  const field = useRef<TextFieldHandle>(null);
  const left = ENGRAVING_MAX - state.engravingText.length;
  usePreviewFonts();

  return (
    <div className="flex flex-col gap-5">
      <TextField
        ref={field}
        label="Engraving Text"
        hint={`Engraved on the inside of the band — tilt the ring to see it. ${left} of ${ENGRAVING_MAX} characters left.`}
        value={state.engravingText}
        maxLength={ENGRAVING_MAX}
        placeholder="Enter engraving text..."
        onChange={(v) => void r.setEngravingText(v)}
      />

      <div className="-mt-3 flex flex-wrap gap-1.5">
        {SYMBOLS.map((sym) => (
          <button
            key={sym}
            type="button"
            title={`Insert ${sym}`}
            aria-label={`Insert the ${sym} symbol`}
            // The caret lives inside TextField's input; mousedown would move it
            // to the button first, so keep the focus where it is.
            onMouseDown={(ev) => ev.preventDefault()}
            onClick={() => field.current?.insert(sym)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-outline-variant/50 font-body text-[14px] leading-none text-on-surface-variant transition-colors hover:border-primary hover:text-primary"
          >
            {sym}
          </button>
        ))}
      </div>

      <div className="flex gap-4">
        <Field label="Font">
          <Select
            value={e.fontFamily}
            options={FONTS}
            onChange={(v) => void r.setEngraving({ fontFamily: v })}
          />
        </Field>
        <Field label="Style">
          <Select
            value={e.fontWeight}
            options={STYLES}
            onChange={(v) => void r.setEngraving({ fontWeight: v as "normal" | "bold" })}
          />
        </Field>
      </div>

      {/* Live sample in the family actually chosen — the picker's own list
          cannot be styled reliably across browsers, and this previews the real
          inscription rather than a font name. Same Google Fonts source the
          viewer draws the decal from, so the two agree. */}
      <div className="-mt-2 flex min-h-[46px] items-center justify-center overflow-hidden rounded-lg border border-outline-variant/40 bg-surface-container-lowest px-3">
        <span
          className="truncate text-[20px] leading-tight"
          style={{ fontFamily: `"${e.fontFamily}", serif`, fontWeight: e.fontWeight, color: e.color }}
        >
          {state.engravingText || "Forever"}
        </span>
      </div>

      {/*
        v42 — sized in multiples of the standard inscription, which is how a
        jeweller states it, not in canvas pixels the customer never sees. The
        WIRE value is still `fontSizePx`: this only changes the unit on screen,
        and `ENGRAVING_BASE_FONT_PX` is the same constant the engine's default
        is built from, so 1.00× is exactly the default engraving.
      */}
      <Slider
        label="Text Size"
        min={0.6}
        max={2}
        step={0.05}
        value={e.fontSizePx / ENGRAVING_BASE_FONT_PX}
        display={(v) => `${v.toFixed(2)}×`}
        minLabel="Fine"
        maxLabel="Large"
        ticks={[1]}
        onChange={(v) => void r.setEngraving({ fontSizePx: Math.round(v * ENGRAVING_BASE_FONT_PX) })}
      />

      <div className="flex flex-col gap-2">
        <SectionLabel>Engraving Side</SectionLabel>
        <div className="flex gap-2">
          {SIDES.map((s) => (
            <Chip
              key={s.id}
              selected={e.side === s.id}
              onClick={() => void apply(r.setEngraving({ side: s.id }))}
              className="flex-1"
            >
              {s.label}
            </Chip>
          ))}
        </div>
      </div>

      {/* The reference's "Mark Color" — the ink sitting in the cut. Same field it
          always was; only the name now matches the control it corresponds to. */}
      <Field label="Mark Colour" hint="The inscription's ink. Bump Only ignores it and leaves the mark in bare metal.">
        <ColorField variant="block" value={e.color} onChange={(hex) => void r.setEngraving({ color: hex })} />
      </Field>

      {/* 0–360 like the reference's readout (345° there rather than our −15°).
          `angleDeg` is a modular rotation, so this is a display domain only —
          normalise a saved value from the old range, leave one already in this
          one alone so a drag to 360 doesn't snap the thumb back to 0. */}
      <Slider label="Placement Angle" min={0} max={360} step={1} ticks={5} unit="°"
        value={e.angleDeg >= 0 && e.angleDeg <= 360 ? e.angleDeg : ((e.angleDeg % 360) + 360) % 360}
        onChange={(v) => void r.setEngraving({ angleDeg: v })} />
      <Slider label="Text Rotation" min={-180} max={180} step={1} value={e.rotationDeg} unit="°"
        onChange={(v) => void r.setEngraving({ rotationDeg: v })} />
      <Slider label="Text Width" min={0.5} max={2} step={0.1} value={e.arcScale}
        onChange={(v) => void r.setEngraving({ arcScale: v })} />
      <Slider label="Band-Side Width" min={0.15} max={0.8} step={0.05} value={e.widthScale}
        onChange={(v) => void r.setEngraving({ widthScale: v })} />

      <LogoRows />
      <CustomSettings />
      <InnerDiamond />
    </div>
  );
}

/** Their measured live Bump Scale, and the engine's default when the field is
 * absent (`EngravingConfig.bumpScale`) — the tick the ruler marks. */
const REFERENCE_BUMP_SCALE = 0.002;

/**
 * v43 — the relief block, behind the same "Custom Settings" fold the reference
 * puts it behind: five controls nobody touches on a first pass, and the
 * inscription is already carved without opening it (`bumpMap` defaults ON).
 *
 * Ungated: every field rides the existing `setEngraving` patch, which an older
 * viewer merges and then ignores.
 */
function CustomSettings() {
  const { state, r } = useRing();
  const e = state.engraving;
  const bump = e.bumpMap ?? true;
  const scale = e.bumpScale ?? REFERENCE_BUMP_SCALE;

  return (
    <Disclosure label="Custom Settings" value={bump ? `Relief ${scale.toFixed(4)}` : "Flat"}>
      <ToggleRow label="Bump Map" hint="Cut the mark into the metal so it catches light."
        checked={bump} onChange={(v) => void r.setEngraving({ bumpMap: v })} />
      <ToggleRow label="Bump Only" hint="Relief with no ink — the mark reads by its shading alone."
        checked={e.bumpOnly ?? false} onChange={(v) => void r.setEngraving({ bumpOnly: v })} />
      <ToggleRow label="Parallax" hint="View-dependent depth, so the mark sits below the surface."
        checked={e.parallax ?? false} onChange={(v) => void r.setEngraving({ parallax: v })} />

      {/* draft={false} on both: these reach the engine's shading-only path (or,
          for Bump Scale from zero, a decal rebuild draft mode cannot batch), so
          endDraft()'s full hi-res band rebuild would be pure cost. */}
      <Slider label="Bump Scale" min={0} max={0.01} step={0.0005} draft={false}
        value={scale} display={(v) => v.toFixed(4)} ticks={[REFERENCE_BUMP_SCALE]}
        minLabel="Shallow" maxLabel="Deep" disabled={!bump}
        onChange={(v) => void r.setEngraving({ bumpScale: v })} />
      <Slider label="Mark Roughness" min={0} max={1} step={0.05} draft={false} ticks={5}
        value={e.markRoughness ?? 0.5} minLabel="Matt" maxLabel="Gloss"
        onChange={(v) => void r.setEngraving({ markRoughness: v })} />
    </Disclosure>
  );
}

function ToggleRow({ label, hint, checked, onChange }: {
  label: string; hint: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex flex-col">
        <span className="font-display text-[11px] font-medium uppercase tracking-[0.12em] text-on-surface-variant">
          {label}
        </span>
        <span className="font-body text-[10px] text-outline">{hint}</span>
      </span>
      <Toggle checked={checked} onChange={onChange} label={hint} />
    </div>
  );
}

/**
 * v39 — the stone set into the INSIDE of the band, which the reference also
 * files under Engraving (it shares the inner surface with the text).
 *
 * Gated: `setInnerDiamond` is a brand-new method name a pre-v39 viewer rejects.
 */
function InnerDiamond() {
  const { state, r, apply } = useRing();
  const caps = useCaps();
  if (!caps.ringDiamondsV39) return null;

  const cfg = state.innerDiamond;
  const on = cfg?.enabled ?? false;
  // Mirrors the engine's own default (`gemPlacement.innerStone`) so the slider
  // opens on the size that is already being rendered.
  const sizeMM = cfg?.sizeMM ?? Math.min(1.6, state.widthMM * 0.4);

  return (
    <>
      <div className="flex flex-col gap-2">
        <SectionLabel>Inner Diamond</SectionLabel>
        <div className="flex gap-2">
          {[{ on: false, label: "None" }, { on: true, label: "Diamond" }].map((o) => (
            <Chip
              key={o.label}
              selected={on === o.on}
              onClick={() => void apply(r.setInnerDiamond(o.on))}
              className="flex-1"
            >
              {o.label}
            </Chip>
          ))}
        </div>
      </div>

      {on ? (
        <>
          <Slider label="Stone Size" min={0.5} max={3} step={0.1} value={sizeMM} unit="mm"
            onChange={(v) => void r.setInnerDiamond({ sizeMM: v })} />
          <Slider label="Stone Angle" min={-180} max={180} step={1} value={cfg?.angleDeg ?? 0} unit="°"
            onChange={(v) => void r.setInnerDiamond({ angleDeg: v })} />
        </>
      ) : null}
    </>
  );
}

/** The two logo placements, in the words a jeweller uses. */
export const LOGO_MODES: { id: EngravingLogoMode; label: string }[] = [
  { id: "with-text", label: "Logo + text" },
  { id: "only", label: "Logo only" },
];

/**
 * v67 (band designs W4) — a maker's logo inside the band, beside the
 * inscription or instead of it. The viewer draws it into the same decal and
 * carves it with the same relief.
 *
 * An .svg is flattened HERE to normalised path data (`logoUi.svgFileToLogoPath`
 * — the file is never parsed as a document), which the viewer accepts and
 * which travels in a share link. A picture (PNG / JPEG / WebP / GIF) becomes a
 * Vault asset when the user is signed in; otherwise it stays on this device and
 * is left out of share links — the row says so.
 */
function LogoRows() {
  const { state, r, apply } = useRing();
  const caps = useCaps();
  const [notice, setNotice] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  if (!caps.ringLogoV67) return null;

  const logo = state.engraving.logo ?? null;
  const box = logo?.svg ? logoViewBox(logo.svg) : null;

  const onFile = async (file: File) => {
    setNotice(null);
    setWorking(true);
    try {
      if (/\.svg$/i.test(file.name) || file.type === "image/svg+xml") {
        const res = svgFileToLogoPath(await file.text());
        if (!res.ok) { setNotice(res.reason); return; }
        await apply(r.setEngraving({ logo: logoFrom(logo, res) }));
        if (res.notes.length) setNotice(res.notes.join(" "));
        return;
      }
      const kind = rasterKind(new Uint8Array(await file.slice(0, 16).arrayBuffer()));
      if (!kind) { setNotice("Use an SVG drawing, or a PNG, JPEG, WebP or GIF picture."); return; }
      if (file.size > LOGO_RASTER_MAX_BYTES) { setNotice("That picture is larger than 1.5 MB. Use a smaller one, or an SVG drawing."); return; }
      const dataUrl = await readFileAsDataUrl(file);
      let url = dataUrl;
      if (vaultSignedIn()) {
        try {
          url = (await uploadAsset({ name: file.name, kind: "image", dataUrl })).url;
        } catch {
          setNotice("Could not store the picture in your Vault, so it is kept on this device only and left out of share links.");
        }
      }
      await apply(r.setEngraving({ logo: logoFrom(logo, { url }) }));
    } catch (err) {
      setNotice(`Could not read that file (${(err as Error).message}).`);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <SectionLabel>Logo</SectionLabel>
        <span className="font-body text-[10px] text-outline">{logo ? (logo.svg ? "Drawing" : "Picture") : "None"}</span>
      </div>
      <span className="-mt-2 font-body text-[10px] text-outline">
        A small maker&rsquo;s mark inside the band. An SVG drawing engraves crisply and travels in share links.
      </span>

      {logo ? (
        <div className="flex h-[64px] items-center justify-center rounded-lg border border-outline-variant/40 bg-surface-container-lowest p-2">
          {logo.svg && box ? (
            <svg viewBox={box} height="44" className="max-w-full" aria-label="Logo preview">
              <path d={logo.svg} fill={state.engraving.color || "#444748"} fillRule={logo.fillRule ?? "nonzero"} />
            </svg>
          ) : logo.url ? (
            <img src={logo.url} alt="Logo preview" className="max-h-[44px] max-w-full object-contain" />
          ) : null}
        </div>
      ) : null}

      <FileButton accept=".svg,image/svg+xml,image/png,image/jpeg,image/webp,image/gif" full onFile={(f) => void onFile(f)}>
        {working ? "Reading…" : logo ? "Replace Logo" : "Upload Logo"}
      </FileButton>

      {notice ? <span role="status" className="font-body text-[10px] text-on-surface-variant">{notice}</span> : null}

      {logo ? (
        <>
          {isLocalOnlyLogo(logo) ? (
            <span className="font-body text-[10px] text-on-surface-variant">
              This picture is kept on this device only and is left out of share links. Sign in to keep it
              in your Vault, or upload an SVG drawing.
            </span>
          ) : null}
          <div className="flex gap-2">
            {LOGO_MODES.map((m) => (
              <Chip
                key={m.id}
                selected={logo.mode === m.id}
                onClick={() => void apply(r.setEngraving({ logo: { ...logo, mode: m.id } }))}
                className="flex-1"
              >
                {m.label}
              </Chip>
            ))}
          </div>
          <Slider
            label="Logo Height"
            min={LOGO_HEIGHT.min}
            max={LOGO_HEIGHT.max}
            step={LOGO_HEIGHT.step}
            unit="mm"
            value={Math.min(LOGO_HEIGHT.max, Math.max(LOGO_HEIGHT.min, logo.heightMM))}
            onChange={(v) => void r.setEngraving({ logo: { ...logo, heightMM: v } })}
          />
          <span className="-mt-2 font-body text-[10px] text-outline">Never taller than the band can hold.</span>
          <Button variant="outline" full onClick={() => void apply(r.setEngraving({ logo: null }))}>
            Remove Logo
          </Button>
        </>
      ) : null}
    </div>
  );
}
